import { useState } from 'react'
import { Button, Spinner } from '@heroui/react'

import { errorMessage } from '@/lib/api/api-error'

import type { PosApprovalPolicy, PosApprovalRule, UpdateApprovalPolicyBody } from '../types'
import { ApprovalRequirement } from '../types'
import { branchLabel, requirementLabels } from './pos-format'

interface ApprovalPolicyEditorProps {
  policy: PosApprovalPolicy
  onSave: (body: UpdateApprovalPolicyBody) => Promise<unknown>
  isSaving: boolean
  error?: unknown
  /** Omitted when the signed-in user may read the policy but not change it. */
  canEdit?: boolean
}

type Draft = Record<string, { requirement: ApprovalRequirement; threshold: number | null }>

function toDraft(rules: PosApprovalRule[]): Draft {
  return Object.fromEntries(
    rules.map((rule) => [rule.action, { requirement: rule.requirement, threshold: rule.threshold }]),
  )
}

/**
 * What a cashier may do alone, and what needs a manager.
 *
 * Edited as a whole and saved in one PUT. Nine switches that each patched
 * themselves would race the moment two managers had this open — the second
 * save silently reverting the first's other eight — so the draft is local and
 * the write is one statement about all of it.
 *
 * The scope line above the rows is not decoration. `source` tells us whether an
 * answer is the outlet's own or inherited, and without saying so a manager can
 * change a rule for the whole estate believing they changed one branch.
 */
export function ApprovalPolicyEditor({
  policy,
  onSave,
  isSaving,
  error,
  canEdit = true,
}: ApprovalPolicyEditorProps) {
  const [draft, setDraft] = useState<Draft>(() => toDraft(policy.rules))

  /**
   * Re-seed when the SERVER's answer changes — a different outlet, or the
   * response to a save that normalised something.
   *
   * A value signature rather than object identity: react-query hands back a new
   * object on every refetch, and keying on identity would wipe a half-finished
   * edit each time the cache refreshed. Adjusted during render rather than in
   * an effect, which is React's own guidance for state derived from props — an
   * effect here would paint the stale draft for a frame first.
   */
  const signature = policy.rules
    .map((rule) => `${rule.action}=${rule.requirement}:${rule.threshold ?? ''}`)
    .join('|')
  const scope = `${policy.branchId ?? 'tenant'}#${signature}`
  const [seeded, setSeeded] = useState(scope)

  if (seeded !== scope) {
    setSeeded(scope)
    setDraft(toDraft(policy.rules))
  }

  const isDirty = policy.rules.some((rule) => {
    const current = draft[rule.action]

    return (
      current &&
      (current.requirement !== rule.requirement || (current.threshold ?? null) !== rule.threshold)
    )
  })

  function setRequirement(action: string, requirement: ApprovalRequirement) {
    setDraft((previous) => ({ ...previous, [action]: { ...previous[action], requirement } }))
  }

  function setThreshold(action: string, threshold: number | null) {
    setDraft((previous) => ({ ...previous, [action]: { ...previous[action], threshold } }))
  }

  async function save() {
    await onSave({
      branchId: policy.branchId ?? undefined,
      rules: policy.rules.map((rule) => ({
        action: rule.action,
        requirement: draft[rule.action]?.requirement ?? rule.requirement,
        // Only the actions that carry a cap send one. The API rejects a
        // threshold on an action that has none rather than ignoring it.
        threshold: rule.hasThreshold ? (draft[rule.action]?.threshold ?? null) : undefined,
      })),
    })
  }

  return (
    <div className="flex flex-col gap-3">
      <p className="text-muted text-xs">
        {policy.branchId ? (
          <>
            Editing <span className="font-medium">{branchLabel(policy.branch)}</span> — overrides the
            tenant default for this outlet only.
          </>
        ) : (
          'Editing the tenant default — applies to every outlet without its own override.'
        )}
      </p>

      <div className="border-border bg-surface divide-border divide-y rounded-2xl border">
        {policy.rules.map((rule) => {
          const current = draft[rule.action] ?? {
            requirement: rule.requirement,
            threshold: rule.threshold,
          }

          return (
            <div
              key={rule.action}
              className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 sm:px-5"
            >
              <span className="text-sm font-medium">{rule.label}</span>

              <div className="flex items-center gap-2">
                {rule.hasThreshold && (
                  <label className="flex items-center gap-1">
                    <span className="sr-only">{rule.label} limit, percent</span>
                    <input
                      type="number"
                      min={0}
                      max={100}
                      step={1}
                      // Not a NumberField: this sits inline in a row of
                      // toggles, and the stepper chrome would make the
                      // thresholded row twice the height of the eight others.
                      className="border-border bg-background h-8 w-16 rounded-lg border px-2 text-right text-xs tabular-nums"
                      value={current.threshold ?? ''}
                      disabled={
                        !canEdit || current.requirement === ApprovalRequirement.Free || isSaving
                      }
                      onChange={(event) => {
                        const raw = event.target.value

                        setThreshold(rule.action, raw === '' ? null : Number(raw))
                      }}
                    />
                    <span className="text-muted text-xs">%</span>
                  </label>
                )}

                <RequirementToggle
                  value={current.requirement}
                  isDisabled={!canEdit || isSaving}
                  onChange={(requirement) => setRequirement(rule.action, requirement)}
                  label={rule.label}
                />
              </div>
            </div>
          )
        })}
      </div>

      {error != null && (
        <p role="alert" className="text-danger text-xs">
          {errorMessage(error, 'Could not save the policy. Please try again.')}
        </p>
      )}

      {canEdit && (
        <div className="flex items-center justify-end gap-2">
          {isDirty && <span className="text-muted text-xs">Unsaved changes</span>}
          <Button
            variant="primary"
            size="sm"
            className="h-9 rounded-full px-4 font-medium"
            isDisabled={!isDirty || isSaving}
            onPress={() => void save()}
          >
            {isSaving && <Spinner size="sm" />}
            {isSaving ? 'Saving…' : 'Save policy'}
          </Button>
        </div>
      )}
    </div>
  )
}

/**
 * Free | Manager, as a two-segment control.
 *
 * Not a switch. "On" and "off" say nothing about which way is which here, and a
 * manager reading a column of nine switches has to remember that on means
 * locked — the two words say it themselves.
 */
function RequirementToggle({
  value,
  onChange,
  isDisabled,
  label,
}: {
  value: ApprovalRequirement
  onChange: (value: ApprovalRequirement) => void
  isDisabled?: boolean
  label: string
}) {
  return (
    <div
      role="group"
      aria-label={`${label} — who may do it`}
      className="border-border bg-background inline-flex overflow-hidden rounded-full border"
    >
      {[ApprovalRequirement.Free, ApprovalRequirement.Manager].map((option) => (
        <button
          key={option}
          type="button"
          disabled={isDisabled}
          aria-pressed={value === option}
          onClick={() => onChange(option)}
          className={`px-3 py-1 text-xs transition-colors disabled:opacity-50 ${
            value === option
              ? 'bg-foreground text-background font-medium'
              : 'text-muted hover:text-foreground'
          }`}
        >
          {requirementLabels[option]}
        </button>
      ))}
    </div>
  )
}
